import React, { useEffect, useState } from "react";
import Button from "./form/button";
import { motion } from "framer-motion";
import { Tooltip, TooltipContent, TooltipProvider } from "./ui/tooltip";
import { TooltipTrigger } from "@radix-ui/react-tooltip";

interface ModeSwitchProps {
    className?: string;
}

export default function ModeSwitch({ className }: ModeSwitchProps) {
    const [darkMode, setDarkMode] = useState<boolean>(false);
    function handleSwitchDarkMode() {
        let newMode = !darkMode;
        setDarkMode(newMode);

        localStorage.setItem("theme", newMode ? "dark" : "light");

        newMode ? document.documentElement.classList.add("dark") : document.documentElement.classList.remove("dark");
    }
    useEffect(() => {
        if (typeof window !== "undefined") {
            if (localStorage.theme === "dark" || (!("theme" in localStorage) && window.matchMedia("(prefers-color-scheme: dark)").matches)) {
                document.documentElement.classList.add("dark");
                setDarkMode(true);
            } else {
                document.documentElement.classList.remove("dark");
                setDarkMode(false);
            }
        }
    }, []);

    function handleKeyPress(event: KeyboardEvent) {
        if (event.altKey && event.code === "KeyT") {
            event.preventDefault();
            handleSwitchDarkMode();
        }
    }

    useEffect(() => {
        document.addEventListener("keydown", handleKeyPress);

        return () => {
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, [handleKeyPress]);

    useEffect(() => {
        const themeColorMetaTag = document.querySelector('meta[name="theme-color"]');
        if (themeColorMetaTag) {
            themeColorMetaTag.setAttribute("content", darkMode ? "#10182A" : "#fff");
        }
    }, [darkMode]);
    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger className="flex items-center justify-center">
                    <Button size="ghost" variant="ghost" onClick={() => handleSwitchDarkMode()} className={className}>
                        {darkMode ? (
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ rotate: 180, scale: 1 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 260,
                                    damping: 20
                                }}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 text-yellow-200">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                                </svg>
                            </motion.div>
                        ) : (
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ rotate: 360, scale: 1 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 260,
                                    damping: 20
                                }}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 text-blue-500">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                                </svg>
                            </motion.div>
                        )}
                    </Button>
                </TooltipTrigger>
                <TooltipContent>
                    <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                        <span className="text-xs">ALT</span> + T
                    </kbd>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
